/**
 * Conseils conditionnels attachés aux réponses d'une question de questionnaire.
 *
 * Une règle : `{ combinaison: 'toutes'|'auMoinsUne', requises: [], exclues: [], texte }`
 *  - `requises` : réponses qui doivent être retenues, toutes ou au moins une
 *  - `exclues`  : réponses qui doivent ne pas l'être
 *
 * Ces fonctions vivent hors des composants parce que la même règle est lue à
 * quatre endroits : les deux écrans où le client répond, l'aperçu du
 * questionnaire et le formulaire qui la crée. Recopiée quatre fois, elle
 * divergerait à la première évolution — et un écran afficherait un conseil que
 * l'autre tairait.
 */

import { libelleOption, valeurOption } from '../../shared/question-options.js'

/** Une règle sans condition ne s'affiche jamais : elle relèverait de l'indice. */
export function regleRemplie(regle, reponsesRetenues) {
  const requises = regle?.requises || []
  const exclues = regle?.exclues || []
  if (!requises.length && !exclues.length) return false

  if (exclues.some((label) => reponsesRetenues.includes(label))) return false

  if (!requises.length) return true
  return regle.combinaison === 'auMoinsUne'
    ? requises.some((label) => reponsesRetenues.includes(label))
    : requises.every((label) => reponsesRetenues.includes(label))
}

/**
 * Textes des conseils à afficher, dans l'ordre défini à la création. Plusieurs
 * peuvent l'être en même temps : c'est voulu, l'ordre de la liste fait foi.
 */
export function conseilsPourReponses(regles, reponsesRetenues) {
  if (!Array.isArray(regles) || !regles.length) return []
  if (!reponsesRetenues.length) return []
  return regles
    .filter((regle) => regleRemplie(regle, reponsesRetenues))
    .map((regle) => regle.texte)
    .filter(Boolean)
}

/**
 * Réponses retenues, quel que soit le type de question : les cases à cocher
 * remplissent `selectedOptions`, les réponses uniques peuvent n'avoir que
 * `answer` (données antérieures au tableau).
 *
 * ELLES SONT RENDUES EN LIBELLÉS, parce que c'est en libellés que les règles
 * désignent leurs conditions (voir `decrireCondition`, qui les cite telles
 * quelles au créateur de la règle). Tant que toutes les options étaient des
 * chaînes nues, valeur et libellé se confondaient et la question ne se posait
 * pas. La case « Autre » a rompu cette égalité — sa valeur est réservée
 * (`__autre__`), son libellé se choisit — et une règle posée sur elle ne se
 * serait jamais déclenchée. Les options `{ label, value }` d'avant en
 * profitent : elles souffraient déjà du même écart, sans que personne l'ait vu.
 *
 * @param {object} reponse `{ answer, selectedOptions }`
 * @param {object} [question] la question posée, pour retrouver les libellés.
 *   Absente, les valeurs sont rendues telles quelles — ce qui reste juste
 *   partout où les options sont des chaînes nues.
 */
export function reponsesRetenues(reponse, question) {
  const choisies = reponse?.selectedOptions
  const retenues = (Array.isArray(choisies) && choisies.length)
    ? choisies
    : (reponse?.answer ? [reponse.answer] : [])

  const options = Array.isArray(question?.options) ? question.options : []
  if (!options.length) return retenues

  return retenues.map((valeur) => {
    const option = options.find((o) => valeurOption(o) === valeur)
    return option ? libelleOption(option) : valeur
  })
}

const citer = (labels) => labels.map((label) => `« ${label} »`).join(', ')

/**
 * Condition d'une règle, en français. Sert à relire ce qu'on vient de
 * paramétrer : c'est cette phrase qu'on vérifie, pas les boutons du formulaire.
 */
export function decrireCondition(regle) {
  const requises = regle?.requises || []
  const exclues = regle?.exclues || []
  const morceaux = []

  if (requises.length === 1) {
    morceaux.push(`${citer(requises)} est cochée`)
  } else if (requises.length > 1) {
    morceaux.push(
      regle.combinaison === 'auMoinsUne'
        ? `au moins l'une de ${citer(requises)} est cochée`
        : `${citer(requises)} sont toutes cochées`,
    )
  }

  if (exclues.length) {
    morceaux.push(exclues.length === 1 ? `${citer(exclues)} ne l'est pas` : `aucune de ${citer(exclues)} ne l'est`)
  }

  return morceaux.join(' et que ')
}

/** La règle entière relue, condition et conseil ensemble. */
export function decrireRegle(regle) {
  const condition = decrireCondition(regle)
  if (!condition) {
    return 'Choisissez au moins une condition, sinon ce conseil ne s’affichera jamais.'
  }
  return regle.texte
    ? `Si ${condition}, le client lit : « ${regle.texte} »`
    : `Si ${condition}, le conseil ci-dessus s'affichera.`
}
